/*
  Norse Quiz — the review carousel's arrows.

  Progressive enhancement only. The track is a native horizontal scroller with snap
  points, so with this file blocked the reviews are still all reachable by swiping or by
  the keyboard; all this adds is the two buttons and their disabled state.

  It scrolls by one card plus one gap, both measured from the DOM rather than from a
  constant, because the card width differs per breakpoint and the section is a merchant
  block list — a hard-coded 364.9 would silently desynchronise the moment either changes.
*/
(function () {
  if (customElements.get('norse-quiz-carousel')) return;

  var NorseQuizCarousel = function () {
    return Reflect.construct(HTMLElement, [], NorseQuizCarousel);
  };
  NorseQuizCarousel.prototype = Object.create(HTMLElement.prototype);
  NorseQuizCarousel.prototype.constructor = NorseQuizCarousel;
  Object.setPrototypeOf(NorseQuizCarousel, HTMLElement);

  NorseQuizCarousel.prototype.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;

    this.track = this.querySelector('[data-nqba-track]');
    this.prev = this.querySelector('[data-nqba-prev]');
    this.next = this.querySelector('[data-nqba-next]');
    if (!this.track) return;

    var self = this;
    if (this.prev) this.prev.addEventListener('click', function () { self.step(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.step(1); });

    /*
      `scroll` fires many times per swipe and the only work here is reading two numbers
      and toggling an attribute, so it is throttled to a frame rather than debounced —
      a debounce would leave the arrows stale for the whole of a long flick.
    */
    var ticking = false;
    this.track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; self.sync(); });
    }, { passive: true });

    window.addEventListener('resize', function () { self.sync(); });

    /*
      The photographs are lazy-loaded, so at upgrade time the track is barely wider than
      its port and the arrows resolve to "nothing to scroll". A ResizeObserver on the
      track re-runs it as each card settles; without it the nav stays hidden until the
      shopper happens to resize the window.
    */
    if (window.ResizeObserver) {
      new ResizeObserver(function () { self.sync(); }).observe(this.track);
    }

    this.sync();
  };

  /** One card plus one gap, measured live. */
  NorseQuizCarousel.prototype.stride = function () {
    var card = this.track.querySelector('.nqba__card');
    if (!card) return this.track.clientWidth;
    var gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  };

  NorseQuizCarousel.prototype.step = function (direction) {
    this.track.scrollBy({ left: this.stride() * direction, behavior: 'smooth' });
  };

  /*
    Where the track actually rests at each end, which is NOT 0 and scrollWidth-clientWidth.

    The track is padded back into the content column, and `scroll-snap-align: start` on
    the cards means the browser settles with card one snapped to the port — so at rest
    `scrollLeft` equals the padding, 150 on desktop. Testing against 0 left the "previous"
    arrow enabled on arrival, pointing at nothing.

    Both bounds are read off the first and last card instead of from the padding, so a
    breakpoint that changes the inset needs no change here.
  */
  NorseQuizCarousel.prototype.bounds = function () {
    var cards = this.track.querySelectorAll('.nqba__card');
    var hard = this.track.scrollWidth - this.track.clientWidth;
    if (!cards.length) return { min: 0, max: hard };
    var origin = this.track.getBoundingClientRect().left - this.track.scrollLeft;
    return {
      min: cards[0].getBoundingClientRect().left - origin,
      max: Math.min(cards[cards.length - 1].getBoundingClientRect().left - origin, hard)
    };
  };

  /*
    On the phone the row takes the height of the card in view instead of the tallest of
    the ten, so the arrows sit under what the shopper is reading. These quotes run from
    two lines to twelve, which stranded the arrows most of a screen below a short one.

    Desktop shows three cards at once, so there is no single "current" card to measure
    and the inline height is cleared.
  */
  NorseQuizCarousel.prototype.syncHeight = function () {
    if (!window.matchMedia('(max-width: 749px)').matches) {
      this.track.style.height = '';
      return;
    }
    var cards = this.track.querySelectorAll('.nqba__card');
    if (!cards.length) return;
    var edge = this.track.getBoundingClientRect().left;
    var best = null;
    var bestGap = Infinity;
    for (var i = 0; i < cards.length; i++) {
      // Nearest card to the left edge of the port — the one snap has settled on.
      var gap = Math.abs(cards[i].getBoundingClientRect().left - edge);
      if (gap < bestGap) { bestGap = gap; best = cards[i]; }
    }
    if (!best) return;
    /*
      Only write when it actually changes. The ResizeObserver below watches this same
      element, so assigning the height unconditionally would have it call itself back
      every frame for as long as the page is open.
    */
    var next = Math.ceil(best.getBoundingClientRect().height) + 'px';
    if (this.track.style.height !== next) this.track.style.height = next;
  };

  NorseQuizCarousel.prototype.sync = function () {
    var b = this.bounds();
    var x = this.track.scrollLeft;
    // 2px of slack: sub-pixel card widths mean scrollLeft rarely lands exactly on a bound.
    if (this.prev) this.prev.disabled = x <= b.min + 2;
    if (this.next) this.next.disabled = x >= b.max - 2;
    this.toggleAttribute('data-nqba-scrollable', b.max - b.min > 2);
    this.syncHeight();
  };

  customElements.define('norse-quiz-carousel', NorseQuizCarousel);
})();
